// src/hooks/useOrganizationConfig.js
// Lightweight hook to fetch organization-specific configuration from Firestore
// Used by ClientDashboard and FirmPortal to apply CC branding and BEP framework

import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Default configuration for standard/production users
 * Used when organizationId is null/undefined or config fetch fails
 */
const DEFAULT_CONFIG = {
  organizationId: 'default',
  organizationName: 'SavvaiEdge',
  primaryColor: '#1976d2',
  secondaryColor: '#dc004e',
  accentColor: '#ff9800',
  logoUrl: null,
  
  terminology: {
    fcfo: 'Advisor',
    fcfos: 'Advisors',
    client: 'Client',
    clients: 'Clients',
    admin: 'Admin',
    firm: 'Firm'
  },
  
  frameworks: {
    decisionMemo: {
      type: 'standard',
      enabled: true,
      displayName: 'Decision Memo'
    },
    continuousPlanning: {
      enabled: false
    }
  }
};

/**
 * Hook to fetch organization configuration from Firestore
 * 
 * @param {string} organizationId - Organization ID from user.organizationId
 * @returns {object} { config, loading, error }
 * 
 * Usage:
 *   const { user } = useAuth();
 *   const { config, loading } = useOrganizationConfig(user?.organizationId);
 */
function useOrganizationConfig(organizationId) {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchConfig() {
      // If no organizationId or it's 'default', use default config
      if (!organizationId || organizationId === 'default') {
        console.log('[useOrganizationConfig] Using default config (no org or org=default)');
        setConfig(DEFAULT_CONFIG);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        console.log(`[useOrganizationConfig] Fetching config for org: ${organizationId}`);

        // Fetch platform_configs/{organizationId} from Firestore
        const configRef = doc(db, 'platform_configs', organizationId);
        const configSnap = await getDoc(configRef);

        if (!configSnap.exists()) {
          console.warn(`[useOrganizationConfig] No config found for org ${organizationId}, using default`);
          setConfig(DEFAULT_CONFIG);
        } else {
          const orgConfig = configSnap.data();
          console.log(`[useOrganizationConfig] Loaded config for org ${organizationId}:`, orgConfig);
          
          // Merge with defaults to ensure all fields exist
          const mergedConfig = {
            ...DEFAULT_CONFIG,
            ...orgConfig,
            terminology: {
              ...DEFAULT_CONFIG.terminology,
              ...(orgConfig.terminology || {})
            },
            frameworks: {
              ...DEFAULT_CONFIG.frameworks,
              ...(orgConfig.frameworks || {}),
              decisionMemo: {
                ...DEFAULT_CONFIG.frameworks.decisionMemo,
                ...(orgConfig.frameworks?.decisionMemo || {})
              },
              continuousPlanning: {
                ...DEFAULT_CONFIG.frameworks.continuousPlanning,
                ...(orgConfig.frameworks?.continuousPlanning || {})
              }
            }
          };

          setConfig(mergedConfig);
        }

        setLoading(false);

      } catch (err) {
        console.error('[useOrganizationConfig] Error loading config:', err);
        setError(err.message);
        setConfig(DEFAULT_CONFIG); // Fallback to default on error
        setLoading(false);
      }
    }

    fetchConfig();
  }, [organizationId]);

  return {
    config,
    loading,
    error,
    isCustomOrg: config.organizationId !== 'default',
    useBEP: config.frameworks?.decisionMemo?.type === 'business_evolution_pyramid'
  };
}

export default useOrganizationConfig;